import CloseIcon from '@mui/icons-material/Close';
import InstallMobileOutlinedIcon from '@mui/icons-material/InstallMobileOutlined';
import { Button, IconButton, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { isIosDevice, useInstall } from '@/lib/platform/install';
import { InstallDialog } from './InstallDialog';

/** Wait a moment after the page appears, so it doesn't compete with it. */
const SHOW_AFTER_MS = 2500;

/**
 * A small card above the phone's bottom navigation, for signed-in learners:
 * one tap installs on Android; on iPhone it shows the steps. "Not now" asks
 * again in two weeks, and after three times stops asking (Profile still has it).
 */
export function InstallBanner() {
  const { user } = useAuth();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('md'));
  const install = useInstall();
  const [ready, setReady] = useState(false);
  const [steps, setSteps] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setReady(true), SHOW_AFTER_MS);
    return () => window.clearTimeout(t);
  }, []);

  if (!user || !phone || !install.mayShowBanner) return null;

  const onInstall = async () => {
    if ((await install.install()) === 'instructions') setSteps(true);
  };

  return (
    <>
      {ready && (
        <Paper
          elevation={6}
          role="region"
          aria-label="Install TradePrep"
          sx={{ position: 'fixed', left: 8, right: 8, bottom: 'calc(72px + env(safe-area-inset-bottom) + 8px)', zIndex: (t) => t.zIndex.appBar + 1, p: 1.5, borderRadius: 2 }}
        >
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <InstallMobileOutlinedIcon color="primary" />
            <Typography variant="body2" sx={{ flex: 1 }}>
              {install.context === 'in-app' ? (
                <>
                  <strong>Open in {isIosDevice() ? 'Safari' : 'your browser'} to add TradePrep to your home screen.</strong> This window can't install it.
                </>
              ) : (
                <>
                  <strong>Add TradePrep to your home screen.</strong> Opens full screen, like an app.
                </>
              )}
            </Typography>
            <Button variant="contained" size="small" onClick={() => void onInstall()}>
              {install.oneTap ? 'Install' : 'How'}
            </Button>
            <IconButton size="small" aria-label="Not now" onClick={install.dismiss}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Paper>
      )}
      <InstallDialog open={steps} context={install.context} onClose={() => setSteps(false)} />
    </>
  );
}
